import { MenuItem, Select, Typography } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/form/form';
import { Input } from '../../../../components/input/input';
import { InfoTooltip } from '../../../../components/tooltip/info-tooltip';
import { useViewer } from '../../../../context/viewer';
import { LibraryExercise } from '../../exercises/types';
import { ExerciseMaxConversionFormType } from '../utils';

export const ExerciseMaxConversionSheetContent = ({
  form,
  exercises,
  mode,
}: {
  form: UseFormReturn<ExerciseMaxConversionFormType>;
  exercises: LibraryExercise[];
  mode: 'create' | 'edit';
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const disableEdit = mode === 'edit' && !hasPermissions('library.exerciseMaxConversions', 'edit');
  const disableCreate = mode === 'create' && !hasPermissions('library.exerciseMaxConversions', 'create');
  const { control, watch } = form;

  const fromLibraryExerciseId = watch('fromLibraryExerciseId');
  const fromLibraryExercise = exercises.find(
    (exercise) => exercise.id === fromLibraryExerciseId,
  );
  const toLibraryExerciseId = watch('toLibraryExerciseId');
  const toLibraryExercise = exercises.find(
    (exercise) => exercise.id === toLibraryExerciseId,
  );
  const percentage = watch('percentage');

  const tooltipText = {
    fromExercise: t('maxConversion.fromExerciseTooltip'),
    toExercise: t('maxConversion.toExerciseTooltip'),
    percentage: t('maxConversion.percentageTooltip'),
  };

  return (
    <>
      <FormField
        control={control}
        name="fromLibraryExerciseId"
        render={({ field: { value, onChange, ref, ...field } }) => (
          <FormItem>
            <FormLabel
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {t('fromExercise')}
              <InfoTooltip
                description={tooltipText.fromExercise}
                className="ml-2"
              />
            </FormLabel>

            <FormControl>
              <Select
                {...field}
                value={value}
                onChange={onChange}
                disabled={mode === 'edit' || disableCreate}
                fullWidth
                placeholder={t('selectExercise')}
                MenuProps={{
                  sx: {
                    maxHeight: 400,
                  },
                }}
              >
                {exercises.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="toLibraryExerciseId"
        render={({ field: { value, onChange, ref, ...field } }) => (
          <FormItem>
            <FormLabel
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {t('toExercise')}
              <InfoTooltip
                description={tooltipText.toExercise}
                className="ml-2"
              />
            </FormLabel>
            <FormControl>
              <Select
                {...field}
                value={value}
                onChange={onChange}
                disabled={mode === 'edit' || disableCreate}
                fullWidth
                placeholder={t('selectExercise')}
                MenuProps={{
                  sx: {
                    maxHeight: 400,
                  },
                }}
              >
                {exercises.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="percentage"
        disabled={mode === 'edit' ? disableEdit : disableCreate}
        render={({ field: { value, ref, onChange, ...field } }) => (
          <FormItem>
            <FormLabel
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {t('percentage')}
              <InfoTooltip
                description={tooltipText.percentage}
                className="ml-2"
              />
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                ref={ref}
                value={value.toString()}
                type="number"
                onChange={(e) => onChange(parseFloat(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {fromLibraryExercise && toLibraryExercise && percentage ? (
        <Typography>
          Missing <b>{toLibraryExercise.name}</b> max
          <br />
          will be calculated as {percentage}% of
          <br />
          <b>{fromLibraryExercise.name}</b> max
        </Typography>
      ) : null}
    </>
  );
};
